import { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import {
  LayoutDashboard, Users, MessageSquare, TrendingUp, LogOut, Menu,
  ArrowLeft, Star, Shield, BarChart3, MessageCircle, UserCog,
  BookOpen, FileText, Activity, ClipboardList, PieChart, HeartPulse, Lock, MessagesSquare, Settings, Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, exact: true },
  { name: 'Traffic & Visitors', href: '/admin/traffic', icon: Globe },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Live Chat', href: '/admin/chat', icon: MessagesSquare },
  { name: 'Usage Analytics', href: '/admin/usage', icon: BarChart3 },
  { name: 'Deep Analytics', href: '/admin/analytics', icon: PieChart },
  { name: 'Feature Usage', href: '/admin/features', icon: Activity },
  { name: 'Pilot Tracking', href: '/admin/pilot', icon: HeartPulse },
  { name: 'Privacy Monitor', href: '/admin/privacy', icon: Lock },
  { name: 'Code Health', href: '/admin/health', icon: Shield },
  { name: 'Feedback', href: '/admin/feedback', icon: Star },
  { name: 'Audit Log', href: '/admin/audit', icon: Shield },
  { name: 'Broadcasts', href: '/admin/messages', icon: MessageCircle },
  { name: 'Support Team', href: '/admin/team', icon: UserCog },
  { name: 'Tickets', href: '/admin/support', icon: MessageSquare },
  { name: 'Knowledge Base', href: '/admin/knowledge-base', icon: BookOpen },
  { name: 'Templates', href: '/admin/templates', icon: FileText },
  { name: 'Testimonials', href: '/admin/testimonials', icon: ClipboardList },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAdmin = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Not admin');
      const data = await res.json();
      setAdmin(data);
      fetchStats();
    } catch (err) { logger.error("Operation failed:", err);
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkAdmin(); }, [checkAdmin]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
  };

  const handleLogout = async () => {
    await fetch(`${API}/api/admin/logout`, { method: 'POST', credentials: 'include' });
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#060C18] flex items-center justify-center"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isActive = (href, exact) => exact ? location.pathname === href : location.pathname.startsWith(href);

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="admin-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#0A1222] border-r border-white/5 z-[100] transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <img src="/sendpi-icon.png" alt="SendPI" className="h-7 w-auto" />
              <span className="font-space text-xl font-bold text-white">Send<span className="text-cyan-400">PI</span></span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">Admin</span>
            </Link>
          </div>

          <div className="p-4 border-b border-white/5">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/40 text-xs">Users</p>
                <p className="text-white font-medium">{stats?.total_users || 0}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white/40 text-xs">Active Today</p>
                <p className="text-white font-medium">{stats?.active_today || 0}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto scrollbar-thin">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                  isActive(item.href, item.exact) ? 'bg-white/5 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-white/5 space-y-2">
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-white/50 hover:text-red-400 hover:bg-white/5 text-sm">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-[#060C18]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between px-6 py-4">
            <button className="lg:hidden p-2 text-white/70 hover:text-white" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4 ml-auto">
              {stats?.suspicious_ips?.length > 0 && (
                <span className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded-lg border border-red-500/30">
                  {stats.suspicious_ips.length} suspicious IP(s) detected
                </span>
              )}
              <span className="text-white/50 text-sm">{admin?.email}</span>
            </div>
          </div>
        </header>
        <main className="p-6 md:p-8">
          <Outlet context={{ stats, refreshStats: fetchStats, admin }} />
        </main>
      </div>
    </div>
  );
}
